import { datadogLogs } from '@datadog/browser-logs'
import { Capacitor } from '@capacitor/core'
import { Device } from '@capacitor/device'
import { PushNotifications } from '@capacitor/push-notifications'

import Api from 'services/Api'
import { fetchShiftsToConfirm } from 'store/actions/applicationInfo'
import { openModal } from 'store/actions/modals'
import { t } from 'translations'

const generateId = () => String(Math.floor(Math.random() * 1000000))

/**
 * Adds a notification
 * @param {object} notification
 * @param {string} notification._id notification unique ID (will be generated if not provided)
 * @param {string} notification.date will be genereated if not provided
 * @param {string or object} notification.description description of the notification
 * @param {string} notification.icon notification icon
 * @param {string} notification.title title of the notification
 * @param {string} notification.type defaults to success
 */
export const createNotification = notification => {
  return dispatch => {
    const _id = generateId()
    dispatch({
      type: 'ADD_NOTIFICATION',
      notification: { ...notification, _id, date: notification.noDate ? null : new Date() }
    })

    // Add a timeout to remove the notification
    setTimeout(() => {
      // First fade out with a fade out animation of about 300ms
      dispatch({ type: 'FADE_OUT_NOTIFICATION', _id, fadeClass: 'animate__animated animate__fadeOut' })
      // When animation is finished (300ms), delete notification
      setTimeout(() => dispatch(removeNotification(_id)), 300)
    }, notification.delay || 10000)
  }
}

export const removeNotification = _id => ({ type: 'REMOVE_NOTIFICATION', _id })

export const createErrorNotification =
  ({ title, description } = {}) =>
  dispatch => {
    return dispatch(
      createNotification({
        description: description ?? t('APPLICATION_ERROR_DESC'),
        title: title ?? t('APPLICATION_ERROR'),
        type: 'danger',
        isErrorNotification: true
      })
    )
  }

export const customErrorNotification = (dispatch, { description, title }) => {
  return dispatch(createNotification({ description, title, type: 'danger', isErrorNotification: true }))
}

export const customSuccessNotification = (dispatch, { resourceType, isCreating, count = 1 }) => {
  const title = t(`components.notification.${resourceType}.${isCreating ? 'CREATED' : 'UPDATED'}`, { count })
  const description = ''
  return dispatch(createNotification({ description, title, type: 'success' }))
}

// Documentation of the Push API: https://capacitorjs.com/docs/apis/push-notifications#pushnotification
export const handlePushNotification = () => dispatch => {
  // Add an event listener to wait for the device to be ready (Notification service will only be available then)
  document.addEventListener('deviceready', onDeviceReady, false)
  dispatch({ type: 'SET_IS_INITIATING_PUSH' })

  if (!Capacitor.isNativePlatform()) return dispatch({ type: 'SET_PUSH_INITIALIZED' })

  async function onDeviceReady() {
    // For mobile app, run only if the PushNotification service is defined
    if (Capacitor.isNativePlatform()) {
      const register = async () => {
        // Validate that the user allows the push notification
        // https://capacitorjs.com/docs/apis/push-notifications#example
        let permStatus = await PushNotifications.checkPermissions()

        if (permStatus.receive === 'prompt') {
          permStatus = await PushNotifications.requestPermissions()
        }

        if (permStatus.receive === 'granted') {
          await PushNotifications.register()
        }

        dispatch({ type: 'SET_ALLOW_PUSH_NOTIFICATION', allowPushNotification: permStatus.receive === 'granted' })
        dispatch({ type: 'SET_PUSH_INITIALIZED' })
      }

      await register()

      // Register the device with our API
      PushNotifications.addListener('registration', async token => {
        const { platform } = await Device.getInfo()
        const { success } = await Api.post('/push/register', { deviceId: token.value, type: platform === 'ios' ? 'APNS' : 'FCM' })
        if (!success) dispatch(createNotification({ description: t('PUSH_ERROR'), title: t('PUSH_ERROR'), type: 'danger' }))
        dispatch({ type: 'SET_PUSH_INITIALIZED', deviceId: token.value, deviceType: platform === 'ios' ? 'APNS' : 'FCM' })
        dispatch({ type: 'SET_ALLOW_PUSH_NOTIFICATION', allowPushNotification: true })
      })

      // Handle the notification
      PushNotifications.addListener('pushNotificationActionPerformed', async ({ notification }) => {
        const { _id, status, type } = notification.data.additionalData
        // Change to route the user to the right page
        switch (type) {
          case 'bulletin': {
            // Transit to the bulletin page
            history.push(`/my/messages`)
            break
          }
          case 'attendance': {
            // Transit to the attendance's page
            status === 'DELETED' ? history.push(`/my/attendance`) : history.push(`/my/attendance/${_id}`)
            break
          }
          case 'availability': {
            const isDeleted = status === 'DELETED'
            const redirectTo = isDeleted ? '/my/availability' : `/my/availability/${_id}`

            window.location.search = `redirectTo=${redirectTo}`

            // Transit to the attendance's page
            break
          }
          case 'shiftConfirmation': {
            const shiftsToConfirm = await dispatch(fetchShiftsToConfirm())
            dispatch(openModal('EmployeeShiftConfirmationModal', shiftsToConfirm))
            break
          }
          case 'shiftOffer': {
            // Transit to the shift offer page
            history.push(`/my/shiftoffers`)
            break
          }
          case 'timeoff': {
            // Transit to the time off's page
            status === 'DELETED' ? history.push(`/my/timeoff`) : history.push(`/my/timeoff/${_id}`)
            break
          }
          case 'shift': {
            history.push(`/my/schedule`)
            break
          }
          default:
            break
        }
      })

      // Handle the push notification error
      PushNotifications.addListener('registrationError', err => {
        // Is a simulator, push does not work
        if (err.message === ' - remote notifications are not supported in the simulator') return dispatch({ type: 'SET_PUSH_INITIALIZED' })
        datadogLogs.logger.error(err.message, undefined, err)
        dispatch({ type: 'SET_PUSH_INITIALIZED' })
        dispatch(createNotification({ description: t('PUSH_ERROR'), title: t('PUSH_ERROR'), type: 'danger' }))
      })
    } else {
      // Not a device with push notification
      dispatch({ type: 'SET_PUSH_INITIALIZED' })
    }
  }
}
